// ----------------------------------------------------------------------------
// Component: Gift Card Balance
// ----------------------------------------------------------------------------
// Component used to activate legacy Gift Cards and check current Shopify Gift Card Balance
// -Contains optional heading, text, and input field for the gift card code.
// -Required PIN field shows up upon entering a gift card code with more than 8 characters
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import JustValidate from "just-validate";

// Style
import "./_gift-card-balance.scss";

// -------------------------------------
// Functionality
// -------------------------------------

/**
 * Set selectors and store default values
 */
const GIFT_CARD = {
    title: document.querySelector(".gift-card-balance__title"),
    body: document.querySelector(".gift-card-balance__body"),
    form: document.querySelector(".gift-card-balance__form"),
    code_field_label: document.querySelector("label[for='gift-card-code']"),
    code_field: document.querySelector(".gift-card-code"),
    pin_field_label: document.querySelector("label[for='pin-card-code']"),
    pin_field: document.querySelector(".gift-card-pin"),
    customerId: document.querySelector(".customerId"),
    error_message: document.querySelector(".gift-card-balance__form .error"),
    success_message: document.querySelector(
        ".gift-card-balance__form .success"
    ),
    nobalance_redirect: document.querySelector(".no-balance-redirect"),
    status_block: document.querySelector(".gift-card-balance__status"),
    balance: document.querySelector(
        ".gift-card-balance__status .current-balance--value"
    ),
    reset_button: document.querySelector(".gift-card-balance__status__reset"),
};

const GIFT_CARD_DEFAULTS = {
    default_title: GIFT_CARD.title?.innerHTML,
    default_body: GIFT_CARD.body?.innerHTML,
};

const moneyFormatter = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
});

/**
 * Fetch Gift Card Data from External API
 */
const getGiftCard = (giftCardData) => {
    const api_endpoint = "https://gift-card-service.shave.io/activate";
    const request = fetch(api_endpoint, {
        method: "POST",
        headers: {
            "Access-Control-Allow-Origin": "*",
            "Content-Type": "application/json",
        },
        body: JSON.stringify(giftCardData),
    })
        .then((response) => {
            if (response.ok) {
                return response.json();
            } else {
                return Promise.reject(response);
            }
        })
        .then((response) => {
            // DSC Cannot process Blackhawk gift cards via API
            if (giftCardData.cardNumber.length === 8) {
                throw "Blackhawk_Special_Case";
                // API returns 200, even if the response is an error
            } else if (response.hasOwnProperty("error")) {
                let errorResponse = new Error("API Error Response");
                errorResponse.status = 400;
                errorResponse.data = response;
                throw errorResponse;
            } else {
                GIFT_CARD.title.innerHTML = "Success";
                GIFT_CARD.body.innerHTML = GIFT_CARD.success_message.value;
                GIFT_CARD.balance.innerHTML = moneyFormatter.format(
                    response.balance
                );

                GIFT_CARD.form.classList.add("js--hidden");
                GIFT_CARD.error_message.classList.add("js--hidden");
                GIFT_CARD.status_block.classList.remove("js--hidden");
            }
        })
        .catch((errorResponse) => {
            let feedback_error;
            if (errorResponse === "Blackhawk_Special_Case") {
                feedback_error = `${window.translations.giftCardGeneralError}
                        <a target="_blank" href="${window.translations.giftCardClubProLink}">
                            ${window.translations.giftCardClubProText}
                        </a>`;
            } else if (errorResponse?.data?.error) {
                switch (errorResponse.data.error) {
                    case "failure_check_balance":
                        feedback_error =
                            window.translations.giftCardBalanceFailure;
                        break;
                    case "error_check_balance":
                        if (errorResponse.data.error_code === "03") {
                            feedback_error =
                                window.translations.giftCardCodeError;
                        } else if (errorResponse.data.error_code === "20") {
                            feedback_error =
                                window.translations.giftCardPinError;
                        } else {
                            feedback_error = `${window.translations.giftCardGeneralError}
                                    <a target="_blank" href="${window.translations.giftCardClubProLink}">
                                        ${window.translations.giftCardClubProText}
                                    </a>`;
                        }
                        break;
                    case "error_card_no_balance":
                        feedback_error = `
                        ${window.translations.giftCardNoBalanceError}
                        <a target="_blank" href="${GIFT_CARD.nobalance_redirect.value}">
                            ${window.translations.giftCardNoBalanceErrorLink}
                        </a>`;
                        break;
                    default:
                        feedback_error = `${window.translations.giftCardGeneralError}
                                <a target="_blank" href="${window.translations.giftCardClubProLink}">
                                    ${window.translations.giftCardClubProText}
                                </a>`;
                        console.error(errorResponse.data.error);
                }
            }

            if (feedback_error !== "") {
                GIFT_CARD.error_message.innerHTML = feedback_error;
                GIFT_CARD.error_message.classList.remove("js--hidden");
                return;
            }

            return console.error(errorResponse);
        });
};

/**
 * Form Validation Rules
 */
if (GIFT_CARD.form) {
    const validator = new JustValidate(GIFT_CARD.form, {
        validateBeforeSubmitting: true,
    });
    validator
        .addField(GIFT_CARD.code_field, [
            {
                rule: "required",
            },
            {
                rule: "minLength",
                value: 8,
            },
            {
                rule: "maxLength",
                value: 19,
            },
            {
                rule: "customRegexp",
                value: /^\w+$/,
                errorMessage: window.translations.giftCardFormatError,
            },
        ])
        .onSuccess((e) => {
            getGiftCard({
                cardNumber: GIFT_CARD.code_field.value,
                cardPin: GIFT_CARD?.pin_field?.value,
                customerId: GIFT_CARD?.customerId?.value,
            });
        });

    /**
     * Gift Card Pin Field Handler
     */
    const pinFieldToggle = (e) => {
        const userInput = e.target.value;

        if (userInput.length > 8) {
            GIFT_CARD.code_field_label;
            GIFT_CARD.pin_field.parentElement.classList.remove("js--hidden");
            GIFT_CARD.pin_field.removeAttribute("disabled");

            validator.addField(GIFT_CARD.pin_field, [
                {
                    rule: "required",
                },
                {
                    rule: "number",
                },
                {
                    rule: "minLength",
                    value: 4,
                },
                {
                    rule: "maxLength",
                    value: 4,
                },
            ]);
        } else {
            GIFT_CARD.pin_field.parentElement.classList.add("js--hidden");
            GIFT_CARD.pin_field.setAttribute("disabled", "");
            validator
                .addField(GIFT_CARD.pin_field, [{ rule: "required" }])
                .removeField(GIFT_CARD.pin_field);
        }
    };
    GIFT_CARD.code_field.addEventListener("input", pinFieldToggle);
}

/**
 * Resets the Gift Card Form for another query
 */
GIFT_CARD.reset_button?.addEventListener("click", (e) => {
    e.preventDefault();
    GIFT_CARD.title.innerHTML = GIFT_CARD_DEFAULTS.default_title;
    GIFT_CARD.body.innerHTML = GIFT_CARD_DEFAULTS.default_body;
    GIFT_CARD.form.reset();

    GIFT_CARD.status_block.classList.add("js--hidden");
    GIFT_CARD.form.classList.remove("js--hidden");
    GIFT_CARD.code_field.focus();
});
